import { createContext, use, useCallback, useMemo, useState } from "react";

type TrpcErrorType = "version-read-only";

const TrpcErrorNotificationContext = createContext<{
  activeErrors: Set<TrpcErrorType>;
  dismiss: (errorType: TrpcErrorType) => void;
  notify: (errorType: TrpcErrorType) => void;
} | null>(null);
TrpcErrorNotificationContext.displayName = "TrpcErrorNotificationContext";

export const TrpcErrorNotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeErrors, setActiveErrors] = useState<Set<TrpcErrorType>>(() => new Set());

  const notify = useCallback((errorType: TrpcErrorType) => {
    setActiveErrors((prev) => {
      if (prev.has(errorType)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(errorType);
      return next;
    });
  }, []);

  const dismiss = useCallback((errorType: TrpcErrorType) => {
    setActiveErrors((prev) => {
      if (!prev.has(errorType)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(errorType);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ activeErrors, dismiss, notify }), [activeErrors, dismiss, notify]);

  return <TrpcErrorNotificationContext value={value}>{children}</TrpcErrorNotificationContext>;
};

export const useNotifyTrpcError = (): ((errorType: TrpcErrorType) => void) => {
  const context = use(TrpcErrorNotificationContext);
  if (!context) {
    throw new Error("useNotifyTrpcError must be used within TrpcErrorNotificationProvider");
  }
  return context.notify;
};

export const useIsTrpcErrorActive = (errorType: TrpcErrorType): { isActive: boolean; dismiss: () => void } => {
  const context = use(TrpcErrorNotificationContext);
  if (!context) {
    throw new Error("useIsTrpcErrorActive must be used within TrpcErrorNotificationProvider");
  }

  const { activeErrors, dismiss } = context;
  const isActive = activeErrors.has(errorType);
  const dismissThis = useCallback(() => dismiss(errorType), [dismiss, errorType]);

  return { isActive, dismiss: dismissThis };
};
